import * as React from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"

import { cn } from "utils/shadcn"
import { Button, buttonVariants } from "app/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar p-4 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatWeekdayName: (weekday) => {
          const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
          return weekdays[weekday.getDay()];
        },
      }}
      classNames={{
        root: cn("w-full", defaultClassNames.root),
        months: cn(
          "flex flex-col md:flex-row relative",
          defaultClassNames.months
        ),
        month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
        nav: cn(
          "flex items-center gap-4 w-full absolute top-0 inset-x-0 justify-between",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex items-center justify-center h-[0.9375rem] w-full",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "w-full flex items-center text-[#4D5358] text-xs font-semibold leading-normal justify-end h-[0.9375rem] gap-4",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative has-focus:border-ring border border-transparent hover:border-[#A2A9B0] shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-sm rdp-dropdown",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 opacity-0 cursor-pointer [&>option]:text-[#697077] [&>option]:text-[0.625rem] [&>option]:font-normal [&>option]:leading-normal [&>option]:py-1 [&>option]:px-2 [&>option]:mx-1 [&>option:checked]:bg-[#F2F4F8]",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-semibold",
          captionLayout === "label"
            ? "text-xs"
            : "rounded-sm px-1 flex items-center gap-1 text-xs h-[0.9375rem] [&>svg]:text-[#878D96] [&>svg]:size-[0.4375rem]",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse space-y-3",
        weekdays: cn("flex gap-4", defaultClassNames.weekdays),
        weekday: cn(
          "w-4 text-center text-[#21272A] text-xs font-semibold leading-[0.9rem] flex-1 rounded-md select-none",
          defaultClassNames.weekday
        ),
        week: cn("flex gap-4 w-full mt-3", defaultClassNames.week),
        week_number_header: cn(
          "select-none w-4",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-xs select-none text-[#21272A]",
          defaultClassNames.week_number
        ),
        day: cn(
          "relative w-4 h-3.5 text-center text-[#21272A] text-xs font-light leading-[0.9rem] rounded-sm select-none",
          defaultClassNames.day
        ),
        range_start: cn(
          "rounded-l-md bg-accent",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
        today: cn(
          "bg-[#DEE3FC] w-4 h-3.5 text-center text-[#21272A] rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "w-4 h-3.5 text-center text-[#878D96] aria-selected:text-[#878D96]",
          defaultClassNames.outside
        ),
        disabled: cn(
          "w-4 h-3.5 text-center text-[#878D96] opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none" className={cn("size-[0.4375rem]", className)} {...props}>
              <path d="M1.40039 1.4502L3.50039 3.5502L5.60039 1.4502" stroke="#878D96" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex w-4 h-3.5 items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        // Dropdown: ({ name, value, onChange, options = [], ...props }) => {
        //   const [isOpen, setIsOpen] = React.useState(false);
        //   const dropdownRef = React.useRef<HTMLDivElement>(null);

        //   React.useEffect(() => {
        //     const handleClickOutside = (event: MouseEvent) => {
        //       if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        //         setIsOpen(false);
        //       }
        //     };

        //     document.addEventListener('mousedown', handleClickOutside);
        //     return () => document.removeEventListener('mousedown', handleClickOutside);
        //   }, []);

        //   const handleSelect = (selectedValue: string | number) => {
        //     if (onChange) {
        //       const event = {
        //         target: { value: selectedValue }
        //       } as React.ChangeEvent<HTMLSelectElement>;
        //       onChange(event);
        //     }
        //     setIsOpen(false);
        //   };

        //   const selectedOption = options.find(opt => opt.value === value);

        //   return (
        //     <div ref={dropdownRef} className="relative">
        //       <select
        //         name={name}
        //         value={value}
        //         onChange={onChange}
        //         className="absolute inset-0 opacity-0 cursor-pointer"
        //         {...props}
        //       >
        //         {options.map((option) => (
        //           <option key={option.value} value={option.value}>
        //             {option.label}
        //           </option>
        //         ))}
        //       </select>
              
        //       <div 
        //         className="bg-white w-[5.1875rem] h-[1.5rem] px-1 border-none rounded flex items-center justify-between text-xs cursor-pointer"
        //         onClick={() => setIsOpen(!isOpen)}
        //       >
        //         <span className="truncate text-[#697077]">
        //           {selectedOption?.label || ''}
        //         </span>
        //         <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none" className="size-[0.4375rem] text-[#878D96]">
        //           <path d="M1.40039 1.4502L3.50039 3.5502L5.60039 1.4502" stroke="#878D96" strokeWidth="1.4" strokeLinecap="round"/>
        //         </svg>
        //       </div>
              
        //       {isOpen && (
        //         <div className="absolute top-full left-0 mt-1 bg-white w-[5.1875rem] h-[10.5rem] min-h-[10.5rem] max-h-[10.5rem] border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)] overflow-y-auto z-50">
        //           {options.map((option) => (
        //             <div
        //               key={option.value}
        //               className={cn(
        //                 "px-2 py-1 text-[0.625rem] cursor-pointer hover:bg-[#F2F4F8] text-[#697077]",
        //                 option.value === value && "bg-[#F2F4F8]"
        //               )}
        //               onClick={() => handleSelect(option.value)}
        //             >
        //               {option.label}
        //             </div>
        //           ))}
        //         </div>
        //       )}
        //     </div>
        //   )
        // },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "w-4 h-3.5 px-1 py-0 data-[selected-single=true]:bg-[#5B73F1] data-[selected-single=true]:text-white data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex text-center text-xs min-w-4 flex-col gap-1 leading-[0.9rem] font-light group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        modifiers.today && "hover:bg-[#DEE3FC]",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
